import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

export interface CheckInReasonData {
  /** Lo que dijo el API (409 NeedsReason): cuán lejos está y de qué facility. */
  message: string;
  punchType: 'Arrival' | 'Departure';
  why?: 'Outside' | 'NoGps' | 'NoFacilityLocation';
}

/**
 * Check in / Check out fuera de la facility (o sin GPS): la persona escribe por qué. Se guarda igual, pero no cuenta
 * para el pago hasta que el dueño o un Admin lo apruebe en Check-ins. Se cierra con el motivo o con null.
 */
@Component({
  selector: 'app-check-in-reason-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule],
  template: `
  <form class="cr" (ngSubmit)="send()" data-test="check-in-reason">
    <h2>{{ data.why === 'Outside' ? "You're not at the facility" : data.why === 'NoGps' ? "We couldn't get your location" : 'This facility has no check-in location' }}</h2>
    <p class="msg">{{ data.message }}</p>
    <label class="field">Why are you checking {{ data.punchType === 'Arrival' ? 'in' : 'out' }} from here?
      <textarea name="reason" rows="3" maxlength="500" [(ngModel)]="reason" required
        placeholder="For example: parked at the overflow lot, GPS is off, covering at another site"
        aria-label="Reason" data-test="reason-input"></textarea>
    </label>
    <p class="muted">It's saved now and counts for pay after an owner or Admin approves it.</p>
    <footer>
      <button type="button" class="btn ghost" (click)="ref.close(null)">Cancel</button>
      <button type="submit" class="btn" [disabled]="reason.trim().length < 3" data-test="reason-send">
        Check {{ data.punchType === 'Arrival' ? 'in' : 'out' }} for approval
      </button>
    </footer>
  </form>
  `,
  styles: [`
    .cr { padding: 20px 22px 16px; width: min(460px, 92vw); box-sizing: border-box; display: flex; flex-direction: column; gap: 12px; color: #111827; font-size: 14px; }
    h2 { margin: 0; font-size: 19px; font-weight: 700; }
    .msg { margin: 0; color: #92400e; background: #fffbeb; border-radius: 10px; padding: 10px 12px; line-height: 1.45; }
    .field { display: flex; flex-direction: column; gap: 6px; font-weight: 600; font-size: 13px; color: #334155; }
    textarea { font: inherit; font-size: 14px; border: 1px solid #cbd5e1; border-radius: 10px; padding: 10px 12px; resize: vertical; color: #111827; }
    .muted { margin: 0; color: #64748b; font-size: 12px; }
    footer { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
    .btn { min-height: 44px; padding: 0 16px; border-radius: 8px; border: 0; font: inherit; font-weight: 700; cursor: pointer; background: #2563eb; color: #fff; }
    .btn.ghost { background: #fff; color: #111827; border: 1px solid #cbd5e1; font-weight: 600; }
    .btn:disabled { opacity: .5; cursor: not-allowed; }
  `]
})
export class CheckInReasonDialogComponent {
  data = inject<CheckInReasonData>(MAT_DIALOG_DATA);
  ref = inject(MatDialogRef<CheckInReasonDialogComponent, string | null>);
  reason = '';

  send(): void {
    const r = this.reason.trim();
    if (r.length >= 3) this.ref.close(r);
  }
}
